import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SkeletonCard, SkeletonText } from "@/components/ui/skeleton";
import { superAdminOrgService } from "@/services/superAdminOrgService";
import { organizationMonitoringService } from "@/services/organizationMonitoringService";

type Props = {
  organizationId: string;
  onboardingMetaFromHeader?: any | null;
};

const formatDateShort = (iso: string | null) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "-";
  }
};

export function OrganizationOverviewTab({
  organizationId,
  onboardingMetaFromHeader,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] =
    useState<
      Awaited<ReturnType<typeof superAdminOrgService.getOrgOverview>>["data"]
    >(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      const { data, error } =
        await superAdminOrgService.getOrgOverview(organizationId);
      if (!mounted) return;
      if (error) setError(error.message);
      setData(data);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [organizationId]);

  const onboardingStage = useMemo(() => {
    const meta = data?.onboarding_meta ?? onboardingMetaFromHeader ?? null;
    return organizationMonitoringService.getOnboardingStage(meta);
  }, [data?.onboarding_meta, onboardingMetaFromHeader]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SkeletonCard padding="lg" />
          <SkeletonCard padding="lg" />
          <SkeletonCard padding="lg" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} padding="lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          {error || "Failed to load overview."}
        </CardContent>
      </Card>
    );
  }

  const stat = (label: string, value: string | number | null | undefined) => (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-gray-600">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold">{value ?? 0}</div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Top summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Onboarding</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge
              variant={
                onboardingStage === "Completed" ? "default" : "secondary"
              }
              className={
                onboardingStage === "Completed"
                  ? "bg-green-100 text-green-800 border-green-200"
                  : "bg-yellow-100 text-yellow-800 border-yellow-200"
              }
            >
              {onboardingStage}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Created</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {formatDateShort(data.created_at)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">
              Current Period End
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {formatDateShort(data.current_period_end)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stat("Team Members", data.team_members_count)}
        {stat("Active Agents", data.active_agents_count)}
        {stat("Assistants", data.ai_assistant_instances_count)}
        {stat("AI Outputs", data.ai_outputs_count)}
        {stat("Files", data.files_count)}
        {stat("Knowledge Items", data.knowledge_items_count)}
        {stat("Storage (MB)", data.storage_usage_mb)}
        {stat("Conversations (This Month)", data.conversations_this_month)}
        {stat("AI Generations (This Month)", data.ai_generations_this_month)}
        {stat("API Calls (This Month)", data.api_calls_this_month)}
        {stat("Voice Calls (This Month)", data.voice_calls_this_month)}
        {stat("Voice Minutes (This Month)", data.voice_minutes_used_this_month)}
      </div>

      {/* Recent activity: future enhancement (outputs/threads/audit). */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Coming soon — recent outputs, conversations, and admin actions.
        </CardContent>
      </Card>
    </div>
  );
}

export default OrganizationOverviewTab;
